/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { notifications } from '@/shared/ui/components/basic/Notifications';
import { getGlobalLoadingStore } from '@/context/LoadingContext';
import {
  classifyError,
  formatErrorTitle,
  formatErrorMessage,
  type ErrorSeverity,
} from './error-classification';

export type { ErrorSeverity };
export {
  classifyError,
  classifyKind,
  formatErrorTitle,
  formatErrorMessage,
} from './error-classification';

/** Options to override the classified wording or auto-close of a reported error. */
export interface ReportErrorOptions {
  /** Overrides the classified, localized title. */
  title?: string;
  /** Overrides the classified, localized message. */
  message?: string;
  /** Overrides the classified severity (reserved for future routing). */
  severity?: ErrorSeverity;
  /** Auto-close duration in ms; defaults to 10000. */
  autoClose?: number;
}

const ERROR_NOTIFICATION_DEBOUNCE_MS = 3000;
const DEFAULT_AUTO_CLOSE_MS = 10000;
let lastReportedAt = 0;

/**
 * Force-dismisses the global loading overlay so a failed request never leaves
 * the app stuck behind the full-screen loader.
 *
 * @returns {void}
 */
function forceDismissLoading(): void {
  try {
    const store = getGlobalLoadingStore();
    const state = store?.getState();
    if (state && state.loadingQueue > 0) {
      store.setState({ loadingQueue: 0, isLoading: false, loadingStartTime: null });
    }
  } catch {
    // LoadingContext not initialized yet
  }
}

/**
 * The single imperative error notifier for the app. Classifies the error,
 * resolves localized title/message wording, renders one toast through the
 * shared notification host, and force-dismisses the loading overlay. A single
 * module-level debounce window collapses a burst of failures into one toast.
 *
 * @param error The thrown value to surface.
 * @param opts Optional overrides for title, message, severity, and auto-close.
 * @returns {void}
 */
export function reportError(error: unknown, opts?: ReportErrorOptions): void {
  const now = Date.now();
  if (now - lastReportedAt < ERROR_NOTIFICATION_DEBOUNCE_MS) {
    return;
  }
  lastReportedAt = now;

  notifications.show({
    type: 'error',
    title: opts?.title ?? formatErrorTitle(error),
    message: opts?.message ?? formatErrorMessage(error),
    autoClose: opts?.autoClose ?? DEFAULT_AUTO_CLOSE_MS,
  });

  forceDismissLoading();
}

/**
 * Resets the shared error-notification debounce window. Test-only hook so each
 * test observes its own single toast without bleeding the window across tests.
 *
 * @returns {void}
 */
export function __resetErrorNotificationDebounce(): void {
  lastReportedAt = 0;
}
